import {
  AutocompleteField,
  Button,
  Callout,
  DropdownField,
  Flex,
  FormLayout,
  Modal,
  TextField,
  getCountryList,
  statesByCountry,
  type CountryCode,
  type Item,
} from "@wayflyer/flyui";
import { useEffect, useMemo, useState } from "react";

import { enrichCountryOptions } from "../../utils/enrich-country-options";
import type { AddressNotification, EditableAddressFormValues } from "./types";

export interface AddressManualEntryModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Called with the entered address once it passes validation.
   */
  onSave: (values: EditableAddressFormValues) => void;
  /**
   * Called when the modal is dismissed without saving.
   */
  onCancel: () => void;
  /**
   * Pre-populates the fields when the modal opens.
   */
  initialAddress?: EditableAddressFormValues | null;
  /**
   * Countries to surface first in the country field. When omitted, a set of common
   * countries is shown first, followed by the rest of the list.
   */
  countries?: CountryCode[];
  /**
   * Fields that cannot be edited (rendered disabled and preserved on save).
   */
  lockFields?: Partial<EditableAddressFormValues>;
  /**
   * A status banner rendered at the top of the modal body.
   */
  notification?: AddressNotification;
  /**
   * Modal title. Defaults to "Enter address manually".
   */
  title?: string;
  /**
   * Returns the jurisdiction-specific label for the state/region field for a
   * given country. Defaults to "State".
   */
  getStateLabel?: (countryCode: string) => string;
  /**
   * Returns the jurisdiction-specific label for the postal-code field for a given
   * country. Defaults to "Postal code".
   */
  getPostalCodeLabel?: (countryCode: string) => string;
}

const EMPTY_ADDRESS: EditableAddressFormValues = {
  country_code: "US",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip_code: "",
};

const US_FIVE_DIGIT_ZIP_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

// Shown first when the caller doesn't pass `countries`. Deliberately not shared-ui's exported
// COMMON_COUNTRY_CODES, which also lists AU - adding it here would reorder this picker.
const PRIORITY_COUNTRY_CODES: CountryCode[] = ["US", "GB", "CA", "DE"];

const statesFor = (countryCode: string): Item[] => (statesByCountry as Record<string, Item[]>)[countryCode] ?? [];

export const AddressManualEntryModal = ({
  isOpen,
  onSave,
  onCancel,
  initialAddress,
  countries,
  lockFields,
  notification,
  title = "Enter address manually",
  getStateLabel,
  getPostalCodeLabel,
}: AddressManualEntryModalProps) => {
  const [values, setValues] = useState<EditableAddressFormValues>(EMPTY_ADDRESS);
  const [errors, setErrors] = useState<Partial<Record<keyof EditableAddressFormValues, string>>>({});

  // The modal is permanently mounted by RhfAddressSearchField and seeds its fields when
  // `isOpen` flips. Ported as-is from fefo; mounting it per edit session is follow-up.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setValues({
      country_code: lockFields?.country_code ?? initialAddress?.country_code ?? "US",
      address_line_1: lockFields?.address_line_1 ?? initialAddress?.address_line_1 ?? "",
      address_line_2: lockFields?.address_line_2 ?? initialAddress?.address_line_2 ?? "",
      city: lockFields?.city ?? initialAddress?.city ?? "",
      state: lockFields?.state ?? initialAddress?.state ?? "",
      zip_code: lockFields?.zip_code ?? initialAddress?.zip_code ?? "",
    });
    setErrors({});
  }, [isOpen, initialAddress, lockFields]);

  const countryOptions = useMemo(
    () => enrichCountryOptions(getCountryList(countries ?? PRIORITY_COUNTRY_CODES)),
    [countries]
  );
  const countryStates = statesFor(values.country_code);
  const stateLabel = getStateLabel?.(values.country_code) ?? "State";
  const postalCodeLabel = getPostalCodeLabel?.(values.country_code) ?? "Postal code";

  const clearErrors = (...fields: (keyof EditableAddressFormValues)[]) =>
    setErrors((previous) => {
      if (!fields.some((field) => previous[field] !== undefined)) {
        return previous;
      }
      const next = { ...previous };
      for (const field of fields) {
        delete next[field];
      }
      return next;
    });

  const setField = (field: keyof EditableAddressFormValues, value: string) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    clearErrors(field);
  };

  const handleSave = () => {
    const nextErrors: Partial<Record<keyof EditableAddressFormValues, string>> = {};
    if (!values.country_code) {
      nextErrors.country_code = "Country is required";
    }
    if (!values.address_line_1) {
      nextErrors.address_line_1 = "Address line 1 is required";
    }
    if (!values.city) {
      nextErrors.city = "City is required";
    }
    if (!values.state) {
      nextErrors.state = `${stateLabel} is required`;
    }
    if (!values.zip_code) {
      nextErrors.zip_code = `${postalCodeLabel} is required`;
    } else if (values.country_code === "US" && !US_FIVE_DIGIT_ZIP_REGEX.test(values.zip_code)) {
      nextErrors.zip_code = "Please enter a valid 5-digit ZIP code";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      onSave(values);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="normal" title={title}>
      <Flex justify="center" align="center" gap="6" direction="column" minWidth="100%">
        {notification && (
          <Callout variant={notification.variant} title={notification.title} description={notification.message} />
        )}
        <Flex width="100%" direction="column" gap="4">
          <FormLayout>
            <AutocompleteField<CountryCode>
              name="country_code"
              label="Country"
              placeholder="Select country"
              options={countryOptions}
              controlledValue={values.country_code as CountryCode}
              onChange={(id) => {
                setValues((previous) =>
                  // A state from the previous country is meaningless in the new one; clear it
                  // so validation demands a fresh choice instead of persisting a stale code.
                  previous.country_code === id ? previous : { ...previous, country_code: id, state: "" }
                );
                clearErrors("country_code", "state");
              }}
              disabled={lockFields?.country_code !== undefined}
              error={errors.country_code}
            />
            <TextField
              name="address_line_1"
              label="Address line 1"
              value={values.address_line_1}
              onChange={(event) => setField("address_line_1", event.target.value)}
              disabled={lockFields?.address_line_1 !== undefined}
              error={errors.address_line_1}
            />
            <TextField
              name="address_line_2"
              label="Address line 2"
              optional
              value={values.address_line_2 ?? ""}
              onChange={(event) => setField("address_line_2", event.target.value)}
              disabled={lockFields?.address_line_2 !== undefined}
            />
            <TextField
              name="city"
              label="City"
              value={values.city}
              onChange={(event) => setField("city", event.target.value)}
              disabled={lockFields?.city !== undefined}
              error={errors.city}
            />
            {countryStates.length > 0 ? (
              <DropdownField
                name="state"
                label={stateLabel}
                options={countryStates}
                controlledValue={values.state}
                onChange={(id) => setField("state", id)}
                disabled={lockFields?.state !== undefined}
                error={errors.state}
              />
            ) : (
              <TextField
                name="state"
                label={stateLabel}
                value={values.state}
                onChange={(event) => setField("state", event.target.value)}
                disabled={lockFields?.state !== undefined}
                error={errors.state}
              />
            )}
            <TextField
              name="zip_code"
              label={postalCodeLabel}
              value={values.zip_code}
              onChange={(event) => setField("zip_code", event.target.value)}
              disabled={lockFields?.zip_code !== undefined}
              error={errors.zip_code}
            />
          </FormLayout>
        </Flex>

        <Flex gap="2" width="100%">
          <Button variant="Tertiary" onClick={onCancel} fullWidth>
            Cancel
          </Button>
          <Button variant="Primary" onClick={handleSave} fullWidth>
            Save address
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
